import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Award, Star, Heart, Flame, Crown, Trophy, MessageCircle, MessagesSquare, Users, Lightbulb, Rocket, Sparkles, HandHeart, TrendingUp, Palette, HeartPulse, Leaf, Cpu } from 'lucide-react';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Star, Heart, Flame, Crown, Trophy, MessageCircle, MessagesSquare, Users,
  Lightbulb, Rocket, Sparkles, HandHeart, TrendingUp, Award, Palette, HeartPulse, Leaf, Cpu,
};

export const useBadgeNotifications = () => {
  const { user } = useAuth();
  const subscribedRef = useRef(false);

  useEffect(() => {
    if (!user || subscribedRef.current) return;

    // Defer realtime subscription to not block page load
    const timer = setTimeout(() => {
      if (subscribedRef.current) return;
      subscribedRef.current = true;

      const channel = supabase
        .channel('badge-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'user_badges',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            const { data: badge } = await supabase
              .from('badge_definitions')
              .select('name, description, icon_name')
              .eq('id', payload.new.badge_id)
              .single();

            if (badge) {
              const IconComponent = iconMap[badge.icon_name] || Award;
              toast.success(
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary/20 animate-pulse">
                    <IconComponent className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">🎉 New Badge Earned!</p>
                    <p className="text-sm text-muted-foreground">{badge.name}</p>
                    <p className="text-xs text-muted-foreground/80">{badge.description}</p>
                  </div>
                </div>,
                { duration: 6000, className: 'badge-toast' }
              );
            }
          }
        )
        .subscribe();

      // Store cleanup ref
      (subscribedRef as any)._channel = channel;
    }, 5000); // 5s delay

    return () => {
      clearTimeout(timer);
      subscribedRef.current = false;
      const channel = (subscribedRef as any)._channel;
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);
};
